import { useMemo } from 'react';

/**
 * Position of the open drawer item inside the host's ordered list, plus the
 * host callback that swaps the drawer to another id.
 *
 * Id-based on purpose: the drawer captures `nextId` *before* it PATCHes the
 * current item, so auto-advance survives the current item leaving a filtered
 * list mid-request.
 */
export interface DrawerNavigation {
    /** 0-based position of the current item in the host's ordered list. */
    index: number;
    total: number;
    prevId: string | null;
    nextId: string | null;
    /**
     * Name of the list `index`/`total` count. The position chip stays hidden
     * without it so the header never shows a bare "N of M".
     */
    pileLabel?: string;
    /**
     * Ask the host to open `id` in the drawer. Returns `false` when the id is
     * no longer in the list; the drawer then owns the "All caught up" fallback
     * (some hosts have no toast surface of their own).
     */
    onSelectId: (id: string) => boolean;
}

export type DrawerNavigationPosition = Pick<DrawerNavigation, 'index' | 'total' | 'prevId' | 'nextId'>;

export function deriveDrawerNavigationPosition(
    orderedIds: readonly string[],
    currentId: string | null | undefined,
): DrawerNavigationPosition | null {
    if (!currentId) return null;
    const index = orderedIds.indexOf(currentId);
    if (index < 0) return null;
    return {
        index,
        total: orderedIds.length,
        prevId: index > 0 ? orderedIds[index - 1] : null,
        nextId: index < orderedIds.length - 1 ? orderedIds[index + 1] : null,
    };
}

export function useDrawerNavigation<T extends { id: string }>(
    items: readonly T[],
    currentId: string | null | undefined,
    onSelectId: (id: string) => boolean,
): DrawerNavigation | undefined {
    const position = useMemo(
        () => deriveDrawerNavigationPosition(items.map((item) => item.id), currentId),
        [items, currentId],
    );
    return useMemo(
        () => (position ? { ...position, onSelectId } : undefined),
        [position, onSelectId],
    );
}
